import { Grid } from "@material-ui/core";
import { useContext } from "react";
import styled from "styled-components";
import bporlogo from "../../../images/NIHR-BPoR-Logo-Linear.svg";
import nhslogo from "../../../images/NHS_logo.svg";
import { AppContext } from "../../../context/AppContext";
import DTEBackLink from "../UI/DTEBackLink/DTEBackLink";
import DTEPhaseBanner from "../UI/DTEPhaseBanner/DTEPhaseBanner";
import { ContentContext } from "../../../context/ContentContext";
import DTEContent from "../UI/DTETypography/DTEContent/DTEContent";
import DTELinkButton from "../UI/DTELinkButton/DTELinkButton";
import usePathname from "../../../hooks/usePathname";
import { Stack } from "@mui/material";

const StyledHeader = styled.header`
  margin-top: 0.5em;
  padding-left: 1em;
  padding-right: 1em;
`;

const LanguageSelector = styled(Grid)`
  float: right;
  margin-right: 1em;
`;

const StyledGridElementLeft = styled(Grid)`
  && {
    text-align: left;
  }
`;

const StyledGridElementRight = styled(Grid)`
  && {
    text-align: right;
  }
`;

const StyledLink = styled.a`
  &&& {
    margin: -6px;
    padding: 3px;
    border: 3px solid transparent;
    text-decoration: none;
    display: inline-block;
    &:focus,
    &:hover {
      background-color: transparent;
      border-color: ${(Props) => Props.theme.NIHR.Yellow};
      box-shadow: 0px 0px 3px 3px rgba(0, 0, 0, 0.75);
      text-decoration: none;
    }
  }
`;

const StyledLogoLink = styled.a`
  &&& {
    margin: 0;
    padding: 0;
    border: 3px solid transparent;
    position: relative;
    text-decoration: none;
    &:focus,
    &:hover {
      background-color: transparent;
      border-color: ${(Props) => Props.theme.NIHR.Yellow};
      box-shadow: 0px 0px 3px 3px rgba(0, 0, 0, 0.75);
      text-decoration: none;
    }
  }
`;

const StyledSkipToMain = styled.a`
  & {
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
    &:focus {
      clip: auto;
      height: auto;
      margin: 0;
      overflow: visible;
      position: static;
      background: ${(Props) => Props.theme.NIHR.Yellow};
      white-space: normal;
      width: auto;
    }
  }
`;

const BPORLogo = styled.img.attrs({
  src: `${bporlogo}`,
  alt: "Be Part Of Research Logo",
})`
  width: 411px;
  @media (max-width: 767px) {
    width: 320px;
  }
  @media (max-width: 576px) {
    width: 280px;
  }
  @media (max-width: 440px) {
    width: 216px;
  }
`;

const NHSLogo = styled.img.attrs({
  src: `${nhslogo}`,
  alt: "NHS Logo",
  height: "36",
  width: "100%",
})``;

export default function Header() {
  const { showBacklink } = useContext(AppContext);
  const { setLanguage, language, content } = useContext(ContentContext);

  const pathname = usePathname();
  const pathsNotToShow = [
    "/ForgottenPassword",
    "/MfaLockedOut",
    "/MfaSessionExpired",
    "/MfaSecurityCodeExpired",
    "/MfaChangePhoneNumber",
    "/MfaChangeNumberConfirmEmail",
    "/MfaTokenChallenge",
    "/MfaTokenSetup",
    "/MfaSmsChallenge",
    "/MfaSmsSetup",
  ];
  const shouldShowLanguageSelector = !pathsNotToShow.includes(pathname);

  const LanguageComponent = () => (
    <Stack direction="row" spacing={1} alignItems="center" className="language-selector">
      {language !== "en-GB" ? (
        <DTELinkButton onClick={() => setLanguage("en-GB")}>English</DTELinkButton>
      ) : (
        <DTEContent>English</DTEContent>
      )}
      <DTEContent>|</DTEContent>
      {language !== "cy-GB" ? (
        <DTELinkButton onClick={() => setLanguage("cy-GB")}>Cymraeg</DTELinkButton>
      ) : (
        <DTEContent>Cymraeg</DTEContent>
      )}
    </Stack>
  );

  return (
    <>
      <div className="header-wrapper">
        <StyledHeader className="govuk-width-container">
          <StyledSkipToMain href="#main">{content["accessibility-hidden-skip-to-main"]}</StyledSkipToMain>
          <Grid container alignItems="center" direction="row" justifyContent="flex-start">
            <StyledGridElementLeft item xs={8} sm={6} md={9}>
              {showBacklink ? (
                <DTEBackLink
                  linkText="Be Part of Research"
                  title="Be Part of Research"
                  href="https://bepartofresearch.nihr.ac.uk/"
                />
              ) : (
                <StyledLink
                  target="_blank"
                  href="https://bepartofresearch.nihr.ac.uk/"
                  rel="noreferrer"
                  id="styledLogoLink"
                  aria-label="Be Part of Research, opens in new tab"
                >
                  <BPORLogo />
                </StyledLink>
              )}
            </StyledGridElementLeft>
            <StyledGridElementRight item xs={4} sm={6} md={3}>
              <StyledLogoLink
                rel="noreferrer"
                id="styledLogoLink"
                target="_blank"
                aria-label="NHS, opens in new tab"
                href=" https://nhs.uk/"
              >
                <NHSLogo id="NHSLogo" />
              </StyledLogoLink>
            </StyledGridElementRight>
          </Grid>
          <Grid container alignItems="center" direction="row" justifyContent="flex-start">
            <StyledGridElementLeft item xs={12} sm={8} md={9}>
              <DTEPhaseBanner phase="BETA" url="https://bepartofresearch.nihr.ac.uk/get-in-touch/" />
            </StyledGridElementLeft>
            <StyledGridElementRight item xs={12} sm={4} md={3}>
              <LanguageSelector>{shouldShowLanguageSelector && <LanguageComponent />}</LanguageSelector>
            </StyledGridElementRight>
          </Grid>
        </StyledHeader>
      </div>
    </>
  );
}
